/**
 * Cross-device tracking — dash data-layer client (feature #166).
 *
 * Thin typed wrapper around `apiClient` for the /api/v1/cross-device surface.
 * Mirrors the contract in `apps/api/src/routes/cross-device.ts`:
 *
 *   POST /resolve              — edge call; returns identity_id + link.
 *   POST /events               — record business event against a device.
 *   GET  /identity/:id         — identity detail: links + events.
 *   GET  /identities?limit=N   — paged list, newest last-seen first.
 *   GET  /summary              — rollup for the overview cards.
 *
 * Keep this module React-free so the unit test file can exercise the pure
 * helpers without spinning up a DOM.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Shared types (match the API)
// ---------------------------------------------------------------------------

export type IdentityKind = "email" | "user_id";
export type DeviceKind = "desktop" | "tablet" | "mobile" | "unknown";

export const IDENTITY_KINDS: readonly IdentityKind[] = ["email", "user_id"];
export const DEVICE_KINDS: readonly DeviceKind[] = [
	"desktop",
	"tablet",
	"mobile",
	"unknown",
];

export const DEVICE_KIND_LABELS: Record<DeviceKind, string> = {
	desktop: "Desktop",
	tablet: "Tablet",
	mobile: "Mobile",
	unknown: "Unknown",
};

export const IDENTITY_KIND_LABELS: Record<IdentityKind, string> = {
	email: "Email",
	user_id: "User ID",
};

export interface IdentityRow {
	id: string;
	tenant_id: string;
	identity_kind: IdentityKind;
	identity_key: string;
	created_at: number;
	last_seen_at: number;
}

export interface LinkRow {
	id: string;
	tenant_id: string;
	identity_id: string;
	device_id: string;
	device_kind: DeviceKind;
	ua_hash: string;
	first_seen_at: number;
	last_seen_at: number;
}

export interface EventRow {
	id: string;
	tenant_id: string;
	identity_id: string;
	device_id: string;
	device_kind: DeviceKind;
	event_type: string;
	value_cents: number;
	occurred_at: number;
}

export interface CrossDeviceSummary {
	identities_total: number;
	links_total: number;
	events_total: number;
	cross_device_identities: number;
	by_device_kind: Record<DeviceKind, number>;
	value_cents_total: number;
	last_seen_at: number | null;
}

export interface ResolveResponse {
	identity_id: string | null;
	link_id: string | null;
	device_id: string;
	device_kind: DeviceKind;
	created_identity: boolean;
	created_link: boolean;
	updated_link: boolean;
	rebound: boolean;
	now: number;
}

export interface IdentityDetailResponse {
	identity: IdentityRow;
	links: LinkRow[];
	events: EventRow[];
	device_kinds: string[];
	cross_device: boolean;
}

// ---------------------------------------------------------------------------
// Draft + validation (pure)
// ---------------------------------------------------------------------------

export interface ResolveDraft {
	deviceId: string;
	deviceKind: DeviceKind;
	identityKind: IdentityKind;
	identityValue: string;
}

export const EMPTY_RESOLVE_DRAFT: ResolveDraft = {
	deviceId: "",
	deviceKind: "desktop",
	identityKind: "email",
	identityValue: "",
};

export interface ValidatedResolveDraft {
	deviceId: string;
	deviceKind: DeviceKind;
	identityKind: IdentityKind;
	identityValue: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

const DEVICE_ID_RE = /^[a-z0-9_.-]{1,128}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidDeviceId(v: string): boolean {
	if (typeof v !== "string") return false;
	return DEVICE_ID_RE.test(v.trim());
}

export function isValidEmail(v: string): boolean {
	if (typeof v !== "string") return false;
	const s = v.trim();
	return s.length > 0 && s.length <= 320 && EMAIL_RE.test(s);
}

export function isValidDeviceKind(v: unknown): v is DeviceKind {
	return typeof v === "string" && (DEVICE_KINDS as readonly string[]).includes(v);
}

export function isValidIdentityKind(v: unknown): v is IdentityKind {
	return typeof v === "string" && (IDENTITY_KINDS as readonly string[]).includes(v);
}

export const resolveDraftSchema = {
	safeParse(input: ResolveDraft): ParseResult<ValidatedResolveDraft> {
		const errors: Record<string, string> = {};

		const deviceId = (input.deviceId ?? "").trim();
		if (deviceId.length === 0) {
			errors.deviceId = "Device ID is required.";
		} else if (!isValidDeviceId(deviceId)) {
			errors.deviceId = "Device ID must be 1-128 chars of letters, digits, . _ -.";
		}

		if (!isValidDeviceKind(input.deviceKind)) {
			errors.deviceKind = "Choose a device kind.";
		}

		if (!isValidIdentityKind(input.identityKind)) {
			errors.identityKind = "Choose an identifier kind.";
		}

		const identityValue = (input.identityValue ?? "").trim();
		if (identityValue.length === 0) {
			errors.identityValue = "Identifier is required.";
		} else if (input.identityKind === "email" && !isValidEmail(identityValue)) {
			errors.identityValue = "Enter a valid email (e.g. user@example.com).";
		} else if (identityValue.length > 320) {
			errors.identityValue = "Identifier is too long.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				deviceId,
				deviceKind: input.deviceKind,
				identityKind: input.identityKind,
				identityValue,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function deviceKindBadgeClass(kind: DeviceKind): string {
	switch (kind) {
		case "desktop":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "mobile":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "tablet":
			return "bg-violet-50 text-violet-700 border-violet-200";
		default:
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
	}
}

export function formatTimestamp(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

export function formatMoney(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const frac = (abs % 100).toString().padStart(2, "0");
	return `${sign}$${whole.toLocaleString("en-US")}.${frac}`;
}

export function truncateKey(key: string, head = 6, tail = 4): string {
	if (typeof key !== "string") return "";
	if (key.length <= head + tail + 1) return key;
	return `${key.slice(0, head)}…${key.slice(-tail)}`;
}

export function sortIdentities(
	rows: readonly IdentityRow[],
): IdentityRow[] {
	return rows.slice().sort((a, b) => b.last_seen_at - a.last_seen_at);
}

export function sortLinks(rows: readonly LinkRow[]): LinkRow[] {
	return rows.slice().sort((a, b) => b.last_seen_at - a.last_seen_at);
}

export function sortEvents(rows: readonly EventRow[]): EventRow[] {
	return rows.slice().sort((a, b) => b.occurred_at - a.occurred_at);
}

export function distinctDeviceKinds(rows: readonly LinkRow[]): DeviceKind[] {
	const set = new Set<DeviceKind>();
	for (const r of rows) set.add(r.device_kind);
	return Array.from(set).sort();
}

export function countIdentitiesByIdentityKind(
	rows: readonly IdentityRow[],
): Record<IdentityKind, number> {
	const out: Record<IdentityKind, number> = { email: 0, user_id: 0 };
	for (const r of rows) out[r.identity_kind] = (out[r.identity_kind] ?? 0) + 1;
	return out;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/cross-device";

export async function resolveIdentity(
	payload: ValidatedResolveDraft,
): Promise<ResolveResponse> {
	return apiClient.post<ResolveResponse>(`${BASE}/resolve`, {
		device_id: payload.deviceId,
		device_kind: payload.deviceKind,
		identity: {
			kind: payload.identityKind,
			value: payload.identityValue,
		},
	});
}

export interface RecordEventInput {
	deviceId: string;
	deviceKind: DeviceKind;
	eventType: string;
	valueCents?: number;
}

export async function recordEvent(
	input: RecordEventInput,
): Promise<{ event: EventRow; resolved_identity: boolean }> {
	return apiClient.post(`${BASE}/events`, {
		device_id: input.deviceId,
		device_kind: input.deviceKind,
		event_type: input.eventType,
		value_cents:
			typeof input.valueCents === "number" && Number.isFinite(input.valueCents)
				? Math.max(0, Math.floor(input.valueCents))
				: 0,
	});
}

export async function listIdentities(
	limit = 50,
): Promise<IdentityRow[]> {
	try {
		const res = await apiClient.get<{ identities: IdentityRow[] }>(
			BASE + "/identities",
			{ query: { limit } },
		);
		return res.identities ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function getIdentity(
	id: string,
): Promise<IdentityDetailResponse | null> {
	try {
		return await apiClient.get<IdentityDetailResponse>(
			`${BASE}/identity/${encodeURIComponent(id)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export async function getSummary(): Promise<CrossDeviceSummary | null> {
	try {
		const res = await apiClient.get<{ summary: CrossDeviceSummary }>(
			`${BASE}/summary`,
		);
		return res.summary ?? null;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const crossDeviceQueryKeys = {
	all: ["cross-device"] as const,
	identities: (limit: number) =>
		[...crossDeviceQueryKeys.all, "identities", limit] as const,
	identity: (id: string) =>
		[...crossDeviceQueryKeys.all, "identity", id || "__none__"] as const,
	summary: () => [...crossDeviceQueryKeys.all, "summary"] as const,
};

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

export function upsertIdentityInList(
	list: readonly IdentityRow[],
	row: IdentityRow,
): IdentityRow[] {
	const found = list.findIndex((r) => r.id === row.id);
	if (found === -1) return sortIdentities([row, ...list]);
	const next = list.slice();
	next[found] = row;
	return sortIdentities(next);
}

export function isCrossDevice(links: readonly LinkRow[]): boolean {
	return distinctDeviceKinds(links).length >= 2;
}
